import { z } from 'zod';

import type { Page } from '@/shared/http';
import { defineDataSource } from '@/shared/page';
import { can } from '@/shared/screen';

import { getAutomaticSplit, listAutomaticSplits } from './automatic-split.api';
import { automaticSplitFilters } from './automatic-split.filters';
import { automaticSplitKeys } from './automatic-split.queries';
import {
  automaticSplitListItemSchema,
  automaticSplitListQueryParams,
  automaticSplitOperationSchema,
  type AutomaticSplitListItem,
  type AutomaticSplitListParams,
} from './automatic-split.wire';

// Fontes de leitura (U40/U41): a tela e os blocos leem por aqui, com as chaves de cache das queries.
// Permissão da rota do legado: `BankAccount:ManageAutomaticDebit` estrita (`router.tsx:645-652`).

export const AUTOMATIC_SPLIT_PERMISSION = can('BankAccount', 'ManageAutomaticDebit', { strict: true });

const filterValue = z.union([z.string(), z.array(z.string())]);

/** Params da lista: página (0-based), tamanho, os filtros como estão na URL e o correspondente (chave de cache). */
export const automaticSplitListParams = z.object({
  page: z.number().int().min(0).default(0),
  size: z.number().int().min(1).max(100).default(5),
  filters: z.record(z.string(), filterValue).default({}),
  tenant: z.string().max(64).optional(),
});
export type AutomaticSplitListSourceParams = z.output<typeof automaticSplitListParams>;

export function toWireListParams(params: AutomaticSplitListSourceParams): AutomaticSplitListParams {
  return { page: params.page, size: params.size, filters: automaticSplitFilters.toParams(params.filters) };
}

export const automaticSplitListSource = defineDataSource({
  id: 'automaticSplit.list',
  description:
    'Paged automatic split operations: creditors, borrower, principal amount, outstanding balance, escrow account balance (cents), open/total installments and guarantee percent over the installment; filters by any text, creditor, borrower and escrow account.',
  shape: 'page',
  params: automaticSplitListParams,
  response: automaticSplitListItemSchema,
  permission: AUTOMATIC_SPLIT_PERMISSION,
  sensitivity: 'internal',
  limits: { staleTimeMs: 30_000, maxSize: 20 },
  key: (params) => automaticSplitKeys.list({ tenant: params.tenant ?? null, ...automaticSplitListQueryParams(toWireListParams(params)) }),
  fetch: async (params, { signal }): Promise<Page<AutomaticSplitListItem>> => {
    const response = await listAutomaticSplits(toWireListParams(params), signal);
    const items = response.items ?? [];
    const total = response.total ?? 0;
    return { items, total, pageCount: response.totalPages ?? Math.ceil(total / params.size) };
  },
});

/** Uma operação (o plano de pagamento): `GET …/Operation/{id}` com as parcelas e as transferências. */
export const automaticSplitOperationSource = defineDataSource({
  id: 'automaticSplit.operation',
  description: 'One automatic split operation: creation date, escrow account id, borrower beneficiary and the installment plan with amounts, paid amount, interest, fine, due and payment dates, status and transfers.',
  shape: 'record',
  params: z.object({ id: z.string().min(1).max(64) }),
  response: automaticSplitOperationSchema,
  permission: AUTOMATIC_SPLIT_PERMISSION,
  sensitivity: 'internal',
  limits: { staleTimeMs: 30_000 },
  key: (params) => automaticSplitKeys.detail(params.id),
  fetch: (params, { signal }) => getAutomaticSplit(params.id, signal),
});
